import { type FunctionComponent, type PropsWithChildren, Children, isValidElement, useEffect, useState, useRef, useContext } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDebounce } from 'use-debounce';
import { JumpTabsContext, JumpTabsProvider } from '../../../contexts/jump-tabs/jump-tabs.context';
import {
	buildJumpTabButtonProps,
	findButtonFromAnchor,
	getJumpTabAnchor,
	getUnderlineSettings
} from '../../../helpers/jump-tabs/jump-tabs.helpers';
import { useRouterLocation } from '../../../hooks/routing/location.hook';
import { type JumpTabNavButton } from '../../../types/jump-tabs.types';
import { ClickableElement } from '../../buttons';
import { DropdownMenu } from '../dropdown-menu/dropdown-menu.component';
import { dropDownShadow, jumpTabButton, jumpTabSlideInAnimation } from './jump-tabs.css';

const TAB_SELECT_DEBOUNCE_DELAY = 400;
const BUTTON_CONTAINER_LEFT_PADDING = 24;

type NavBehavior = 'sticky' | 'sticky-scroll' | 'scroll';

type JumpTabButtonProps = {
	label: string;
	selected: boolean;
	onClick: () => void;
	id: string;
};

export const JumpTabButton: FunctionComponent<JumpTabButtonProps> = ({ label, selected, onClick, id }) => (
	<ClickableElement
		ariaLabel={label}
		className={`f5 pa1 pa0 outline-0 ${selected ? 'font-medium' : 'md:font-medium'} ${jumpTabButton}`}
		onClick={onClick}
		id={id}
		testId={id}>
		{label}
	</ClickableElement>
);

export type JumpTabsListProps = {
	navBehavior: NavBehavior;
};

export const JumpTabsList: FunctionComponent<JumpTabsListProps> = ({ navBehavior }) => {
	const { ref: stickyNavRef, inView: showStickyNavInView } = useInView({ initialInView: true });
	const [buttonSettings, setButtonSettings] = useState({
		width: '0px',
		marginLeft: '0px'
	});
	const { selectedTab, setSelectedTab, tabs } = useContext(JumpTabsContext);
	const buttonContainerRef = useRef<HTMLInputElement>(null);
	const [debouncedSelectedTab] = useDebounce(selectedTab, TAB_SELECT_DEBOUNCE_DELAY);
	const { hash } = useRouterLocation();

	useEffect(() => {
		// sets initial tab, scrolls to anchor if present in url otherwise uses first one
		if (hash) {
			setSelectedTab(findButtonFromAnchor(hash.slice(1), tabs), true);
		} else {
			setSelectedTab(tabs[0], false);
		}
	}, []);

	useEffect(() => {
		const handleResize = () => {
			const extraPadding = buttonContainerRef.current?.offsetLeft || 0;
			setButtonSettings(getUnderlineSettings(debouncedSelectedTab, extraPadding + BUTTON_CONTAINER_LEFT_PADDING));
		};
		handleResize();
		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, [debouncedSelectedTab]);

	const isSelected = (button: JumpTabNavButton) => selectedTab.id === button.id;

	const stickyScrollClass = navBehavior === 'sticky-scroll' && !showStickyNavInView ? jumpTabSlideInAnimation : '';
	const navBehaviorClass = navBehavior === 'sticky' ? jumpTabSlideInAnimation : navBehavior === 'sticky-scroll' ? stickyScrollClass : '';

	return (
		<>
			<div ref={stickyNavRef}></div>
			{/* The div below is introduced to offset the height of the menu. It becomes necessary when the menu's position property is switched to 'fixed', as it removes the menu from the document flow, causing layout shifts. */}
			{!showStickyNavInView && <div style={{ height: '69px' }} data-testid="navbar-placeholder"></div>}
			<div className={`bg-theme-white pv2-ns ph2-m w-100 z-3 ${navBehaviorClass}`} data-testid="nav-bar" ref={buttonContainerRef}>
				<div className="dn db-l pv3 ph4">
					<div className="flex justify-between flex-nowrap w-100">
						{tabs.map((button) => (
							<JumpTabButton
								key={`${button.id}-lg`}
								label={button.label}
								selected={isSelected(button)}
								onClick={() => setSelectedTab(button, true)}
								id={button.id}
							/>
						))}
					</div>
					{/* This div's purpose is to house the sliding underline bar.
					The transition setting provides for a more visually pleasing experience */}
					<div className="w-100">
						<div
							data-testid="underline"
							className={`bb bw1`}
							style={{
								// width and margin-left (relative to the target button) are both available from the button dom element,
								// and are used here after calculation to make the sliding bar have the proper width and positioning.
								width: buttonSettings.width,
								marginLeft: buttonSettings.marginLeft,
								transition: 'all 0.5s'
							}}
						/>
					</div>
				</div>
				<div className="flex items-center mh2 dn-l h3">
					<DropdownMenu
						title={debouncedSelectedTab.label}
						shouldCloseOnChoice={true}
						containerClassName="bg-theme-white theme-black pointer relative w-100 z-3"
						itemClassName="bt-0"
						menuSelectClassName="pv1 flex justify-between bb b--theme-black w-100"
						dropdownClassName={`bw0 pl3 pv2 ${dropDownShadow}`}
						onChooseItem={(index) => setSelectedTab(tabs[index], true)}>
						{tabs.map((button) => (
							<div key={`${button.id}-sm`}>{button.label}</div>
						))}
					</DropdownMenu>
				</div>
			</div>
		</>
	);
};

type JumTabProps = {
	tabName: string;
	tabId?: string;
	className?: string;
};

export const JumpTab: FunctionComponent<PropsWithChildren<JumTabProps>> = ({ tabName, tabId, className = '', children }) => {
	const { ref, inView } = useInView({ rootMargin: '80px 0px 0px 0px', threshold: 0.4 });
	const { setSelectedTab } = useContext(JumpTabsContext);
	const anchor = getJumpTabAnchor({ tabName, tabId });

	useEffect(() => {
		if (inView) {
			setSelectedTab(buildJumpTabButtonProps({ tabName, tabId }), false);
		}
	}, [inView]);

	return (
		<div id={anchor} className={className} ref={ref}>
			{children}
		</div>
	);
};

export type JumpTabsProps = {
	className?: string;
	navBehavior?: NavBehavior;
};

export const JumpTabs: FunctionComponent<PropsWithChildren<JumpTabsProps>> = ({
	className = '',
	navBehavior = 'sticky-scroll',
	children
}) => {
	const navButtons = Children.map(children, (child) => {
		if (isValidElement(child)) {
			return buildJumpTabButtonProps(child.props);
		} else {
			return null;
		}
	})?.filter(({ label }) => label);

	return (
		<JumpTabsProvider tabs={navButtons || []}>
			<JumpTabsList navBehavior={navBehavior} />
			<div className={className}>{children}</div>
		</JumpTabsProvider>
	);
};
